import {
  Badge,
  createStyles,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as NavConstants from 'constants/NavConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import navigateToURL from 'lib/navigateToURL';
import AgentChatButton from 'llm/chat-library/foundries/AgentChatButton';
import {
  ChatSettingsTab,
  openRoutineHomepageView,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import {EventTriggerConfig, RoutineStatus, TimeTriggerConfig, TriggerType} from 'llm/routines/RoutineTypes';
import {formatEventTrigger, formatTimeTrigger} from 'llm/routines/RoutineUtils';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  AgentFoundryQuery_agentFoundry_AgentFoundryNode_agents_AgentFoundryAgentNode as FoundryAgentEntry,
  AgentFoundryQuery_agentFoundry_AgentFoundryNode_coordinatorAgent_RoutineNode as FoundryRoutine,
} from './__generated/AgentFoundryQuery';

type AgentFilter = 'ALL' | RoutineStatus;
type AgentKind = 'AGENT' | 'COORDINATOR';

interface FoundryAgent {
  key: string;
  kind: AgentKind;
  routine: FoundryRoutine | null;
}

interface AgentFoundryAgentsProps {
  agents: Array<FoundryAgentEntry>;
  coordinatorAgent: FoundryRoutine | null;
}

const useStyles = createStyles((theme) => ({
  search: {
    flex: '1 1 240px',
    maxWidth: 440,
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 720,
  },
  tableHeader: {
    backgroundColor: theme.colors.gray[0],
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.xs,
    textTransform: 'uppercase',
  },
  clickableRow: {
    cursor: 'pointer',
  },
}));

function getAgentStatus(routine: FoundryRoutine | null): {
  color: string;
  label: string;
} {
  switch (routine?.status) {
    case RoutineStatus.ACTIVE:
      return {color: 'green', label: 'Active'};
    case RoutineStatus.PAUSED:
      return {color: 'gray', label: 'Paused'};
    case RoutineStatus.PENDING_REGISTRATION:
      return {color: 'yellow', label: 'Setting up'};
    default:
      return {color: 'gray', label: 'Unavailable'};
  }
}

function getAgentSchedule(routine: FoundryRoutine | null): string {
  if (routine == null) {
    return 'Details unavailable';
  }
  switch (routine.triggerType) {
    case TriggerType.TIME_BASED:
      return formatTimeTrigger(routine.triggerConfig as TimeTriggerConfig);
    case TriggerType.EVENT_BASED:
      return formatEventTrigger(routine.triggerConfig as EventTriggerConfig);
    default:
      return 'Unknown trigger';
  }
}

function getAgentName(agent: FoundryAgent): string {
  return agent.routine?.name ?? 'Unavailable agent';
}

function openAgent(agent: FoundryAgent, isHomepageAvailable: boolean): void {
  if (agent.routine == null) {
    return;
  }
  if (isHomepageAvailable) {
    openRoutineHomepageView(agent.routine.id);
    SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
    return;
  }
  navigateToURL(chatSettingsModalUrl(ChatSettingsTab.AGENTS, {selectedItemId: agent.routine.id}));
}

/**
 * The roster of agents (the Coordinator agent plus its agents, matching the AgentFoundry schema's
 * own `coordinatorAgent`/`agents` terminology) provisioned for an Agent Foundry: a searchable,
 * status-filterable table showing each agent's schedule and registration status, plus a per-agent
 * "Chat" action that starts the agent running directly with a typed message (see AgentChatButton).
 * The Coordinator row is visually distinguished by its icon (see the icon lookup below); no
 * separate Role column is shown. Clicking a row with a resolvable routine opens its homepage directly
 * in the semi-expanded AI sidepanel when available, or navigates to its config otherwise; a row with
 * no resolvable routine is not clickable, since there is nothing to open.
 *
 * `coordinatorAgent` and each `agents[].agent` are already fully-loaded Routine references from
 * the parent's AgentFoundryQuery -- see that query for why no separate lookup is needed here. A
 * null `agent` on an `agents` entry means the reference is inaccessible or dangling; the slot
 * itself is preserved (see the `AgentFoundryAgent.agent` schema doc), so it renders as an
 * "Unavailable agent" row rather than being dropped. A null `coordinatorAgent`, in contrast,
 * simply means the foundry has no Coordinator yet, so it renders no Coordinator row at all.
 */
export default function AgentFoundryAgents({agents, coordinatorAgent}: AgentFoundryAgentsProps): JSX.Element {
  const {classes} = useStyles();
  const {llmEnableRoutineHomepage} = useRolloutFlags();
  const [filter, setFilter] = useState<AgentFilter>('ALL');
  const [search, setSearch] = useState('');
  const isHomepageAvailable = isAgentsFeatureEnabled() && llmEnableRoutineHomepage;

  const foundryAgents: Array<FoundryAgent> = useMemo(
    () => [
      ...(coordinatorAgent == null
        ? []
        : [{key: 'coordinator', kind: 'COORDINATOR' as const, routine: coordinatorAgent}]),
      ...agents.map((entry, index) => ({
        key: `agent-${entry.agent?.id ?? index}`,
        kind: 'AGENT' as const,
        routine: entry.agent,
      })),
    ],
    [agents, coordinatorAgent]
  );

  const provisionedAgentCount = foundryAgents.length;
  const unavailableAgentCount = foundryAgents.filter((agent) => agent.routine == null).length;

  const counts = useMemo(
    () => ({
      active: foundryAgents.filter((agent) => agent.routine?.status === RoutineStatus.ACTIVE).length,
      all: provisionedAgentCount,
      paused: foundryAgents.filter((agent) => agent.routine?.status === RoutineStatus.PAUSED).length,
      settingUp: foundryAgents.filter((agent) => agent.routine?.status === RoutineStatus.PENDING_REGISTRATION)
        .length,
    }),
    [foundryAgents, provisionedAgentCount]
  );

  const visibleAgents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return foundryAgents.filter((agent) => {
      if (filter !== 'ALL' && agent.routine?.status !== filter) {
        return false;
      }
      if (normalizedSearch === '') {
        return true;
      }
      return [getAgentName(agent), agent.routine?.id]
        .filter((value): value is string => value != null)
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [foundryAgents, filter, search]);

  const chatEnabled = BenchlingConfig.LLM_ENABLE_DIRECT_AGENT_RUNS;

  return (
    <Stack spacing="md">
      <Text size="sm" c="dimmed">
        {counts.all} provisioned · {counts.active} active
      </Text>

      {unavailableAgentCount > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={
            unavailableAgentCount === provisionedAgentCount
              ? `Details are unavailable for all ${provisionedAgentCount} provisioned agents.`
              : `Details are unavailable for ${unavailableAgentCount} of the ${provisionedAgentCount} provisioned agents.`
          }
        />
      )}

      {provisionedAgentCount === 0 ? (
        <Text c="dimmed">No agents have been provisioned for this foundry yet.</Text>
      ) : (
        <>
          <Group position="apart" align="center">
            <TextInput
              aria-label="Search agents"
              className={classes.search}
              icon={<BenchlingIcon icon="search" />}
              placeholder="Search agents"
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
              }}
            />
            <SegmentedControl
              aria-label="Filter agents by status"
              role="radiogroup"
              size="xs"
              value={filter}
              onChange={(value) => {
                setFilter(value as AgentFilter);
              }}
              data={[
                {label: `All ${counts.all}`, value: 'ALL'},
                {label: `Active ${counts.active}`, value: RoutineStatus.ACTIVE},
                {label: `Paused ${counts.paused}`, value: RoutineStatus.PAUSED},
                {
                  label: `Setting up ${counts.settingUp}`,
                  value: RoutineStatus.PENDING_REGISTRATION,
                },
              ]}
            />
          </Group>

          {visibleAgents.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No agents match these filters.
            </Text>
          ) : (
            <Paper withBorder={true} radius="md" className={classes.tableContainer}>
              <Table
                className={classes.table}
                horizontalSpacing="md"
                verticalSpacing="sm"
                highlightOnHover={true}
              >
                <thead className={classes.tableHeader}>
                  <tr>
                    <th>Agent</th>
                    <th>Schedule</th>
                    <th>Status</th>
                    {chatEnabled && <th>Chat</th>}
                  </tr>
                </thead>
                <tbody>
                  {visibleAgents.map((agent) => {
                    const status = getAgentStatus(agent.routine);
                    return (
                      <tr
                        key={agent.key}
                        className={agent.routine == null ? undefined : classes.clickableRow}
                        {...(agent.routine == null
                          ? {}
                          : {
                              'aria-label': `View ${getAgentName(agent)}`,
                              ...getActionButtonRoleProps({
                                onPress: () => {
                                  openAgent(agent, isHomepageAvailable);
                                },
                              }),
                            })}
                      >
                        <td>
                          <Group spacing="sm" noWrap={true}>
                            <ThemeIcon color="violet" variant="light" size={32} radius="sm">
                              <BenchlingIcon
                                icon={agent.kind === 'COORDINATOR' ? 'gears-outline' : 'sparkle'}
                                aria-hidden="true"
                              />
                            </ThemeIcon>
                            <Stack spacing={0}>
                              <Text size="sm" fw={600}>
                                {getAgentName(agent)}
                              </Text>
                              {agent.routine == null && (
                                <Text size="xs" c="dimmed">
                                  Details unavailable
                                </Text>
                              )}
                            </Stack>
                          </Group>
                        </td>
                        <td>
                          <Text size="sm">{getAgentSchedule(agent.routine)}</Text>
                        </td>
                        <td>
                          <Badge color={status.color} variant="light" size="sm">
                            {status.label}
                          </Badge>
                        </td>
                        {chatEnabled && (
                          <td>
                            {agent.routine != null && (
                              // Isolated from the row's own click/keydown handling above, so
                              // opening the Chat popover doesn't also navigate the row.
                              <div
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                                onKeyDown={(event) => {
                                  event.stopPropagation();
                                }}
                                role="presentation"
                              >
                                <AgentChatButton
                                  agentName={getAgentName(agent)}
                                  routineId={agent.routine.id}
                                />
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
